// src/pages/ComplaintDetails.jsx
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getComplaintById, updateComplaintStatus, getReviewById, getUserDetails, getOrderDetails } from '../api';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../components/Icon';
import StatusBadge from '../components/StatusBadge';
import ComplaintProfileAvatar from '../components/ComplaintProfileAvatar';
import '../css/user-details.css'; // Используем стили профиля для Bento Grid
import '../css/complaint-details.css';
import { pushDetailNavState } from '../utils/detailNavState';

// Вспомогательный компонент для строк в стиле профиля пользователя
const InfoRow = ({ label, value, highlight, isLink, onClick }) => (
  <div className="info-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px dashed rgba(0, 0, 0, 0.06)' }}>
    <span className="info-label" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500, flex: 1 }}>
      {label}
    </span>
    {isLink ? (
      <span
        className="info-value hover-trigger"
        onClick={onClick}
        style={{ color: '#3b82f6', fontSize: '0.9rem', fontWeight: 600, flex: 1.5, textAlign: 'right', wordBreak: 'break-all', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}
      >
        {value || '—'}
        <Icon name="external-link" size={14} style={{ opacity: 0.7 }} />
      </span>
    ) : (
      <span className={`info-value ${highlight ? 'highlight' : ''}`} style={{ color: 'var(--text-primary)', fontSize: '0.9rem', fontWeight: 500, flex: 1.5, textAlign: 'right', wordBreak: 'break-all' }}>
        {value || '—'}
      </span>
    )}
  </div>
);

/** Кастомный выбор статуса: анимированное меню + шеврон в отдельной ячейке (не прилипает к краю). */
const ComplaintStatusField = ({ value, options, onChange, disabled, placeholder }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="complaint-status-field" ref={wrapRef}>
      <button
        type="button"
        className="complaint-status-field__trigger"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="complaint-status-field__label">{selected?.label || placeholder}</span>
        <span className={`complaint-status-field__chevron${open ? ' complaint-status-field__chevron--open' : ''}`} aria-hidden>
          <Icon name="chevron-down" size={16} />
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="complaint-status-field__menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={value === opt.value}
                className={`complaint-status-field__option${value === opt.value ? ' complaint-status-field__option--active' : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {value === opt.value && <Icon name="check" size={14} className="complaint-status-field__check" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ComplaintDetails = () => {
  const { complaintId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const statusAnchorRef = useRef(null);
  const [statusPopupPos, setStatusPopupPos] = useState({ top: 0, left: 0 });

  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [message, setMessage] = useState(null);

  const [reporterData, setReporterData] = useState(null);
  const [targetUserData, setTargetUserData] = useState(null);
  const [targetOrderData, setTargetOrderData] = useState(null);
  const [profilesLoading, setProfilesLoading] = useState(false);

  const statusOptions = [
    { value: 'PENDING', label: t('complaints.statuses.PENDING') },
    { value: 'IN_REVIEW', label: t('complaints.statuses.IN_REVIEW') },
    { value: 'RESOLVED', label: t('complaints.statuses.RESOLVED') },
    { value: 'REJECTED', label: t('complaints.statuses.REJECTED') }
  ];

  const targetTypeLabels = {
    USER: t('complaints.targetTypes.USER'),
    COMMENT: t('complaints.targetTypes.COMMENT'),
    ORDER: t('complaints.targetTypes.ORDER')
  };

  const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'ro' ? 'ro-RO' : 'en-US';

  const fetchComplaint = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getComplaintById(complaintId);
      setComplaint(data);
      if (data?.status) {
        setSelectedStatus(data.status);
      }

      // Fetch user profiles for Reporter and Target (if Target is a USER)
      setProfilesLoading(true);
      try {
        const promises = [];
        if (data?.reporterId) {
          promises.push(
            getUserDetails(data.reporterId)
              .then(res => setReporterData(res))
              .catch(e => console.error('Failed to fetch reporter', e))
          );
        }
        if (data?.targetType === 'USER' && data?.targetId) {
          promises.push(
            getUserDetails(data.targetId)
              .then(res => setTargetUserData(res))
              .catch(e => console.error('Failed to fetch target user', e))
          );
        }
        if (data?.targetType === 'ORDER' && data?.targetId) {
          promises.push(
            getOrderDetails(data.targetId)
              .then(res => setTargetOrderData(res))
              .catch(e => console.error('Failed to fetch target order', e))
          );
        }
        await Promise.all(promises);
      } finally {
        setProfilesLoading(false);
      }

    } catch (err) {
      setError(err.message || t('complaintDetails.error'));
      console.error('Error fetching complaint:', err);
    } finally {
      setLoading(false);
    }
  }, [complaintId, t]);

  useEffect(() => {
    fetchComplaint();
  }, [fetchComplaint]);

  const updateStatusPopupPosition = useCallback(() => {
    if (!showStatusPopup || !statusAnchorRef.current) return;
    const r = statusAnchorRef.current.getBoundingClientRect();
    setStatusPopupPos({
      top: r.bottom + 8,
      left: r.left + r.width / 2,
    });
  }, [showStatusPopup]);

  useLayoutEffect(() => {
    updateStatusPopupPosition();
  }, [updateStatusPopupPosition]);

  useEffect(() => {
    if (!showStatusPopup) return;
    const onReposition = () => updateStatusPopupPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [showStatusPopup, updateStatusPopupPosition]);

  useEffect(() => {
    if (!showStatusPopup) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowStatusPopup(false);
        if (complaint?.status != null) setSelectedStatus(complaint.status);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showStatusPopup, complaint]);

  const handleStatusUpdate = async () => {
    if (!selectedStatus || selectedStatus === complaint.status) return;

    try {
      setUpdating(true);
      const updatedComplaint = await updateComplaintStatus(complaintId, selectedStatus);
      setComplaint(updatedComplaint);
      setShowStatusPopup(false);
      setMessage({ type: 'success', text: t('common.saved') });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.message || t('complaintDetails.error'));
      console.error('Error updating status:', err);
      setMessage({ type: 'error', text: err.message || 'Error updating status' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString, fallbackDate) => {
    const dateToFormat = dateString || fallbackDate;
    if (!dateToFormat) return 'N/A';
    try {
      const date = new Date(dateToFormat);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year}, ${hours}:${minutes}`;
    } catch {
      return 'N/A';
    }
  };

  const handleReporterClick = () => {
    if (complaint?.reporterId) {
      navigate(`/users/${complaint.reporterId}`, {
        state: pushDetailNavState(location, { fromComplaint: location.pathname }),
      });
    }
  };

  const loadReview = useCallback(async (reviewId) => {
    try {
      setReviewLoading(true);
      setReviewError(null);
      const data = await getReviewById(reviewId);
      setReview(data);
    } catch (err) {
      setReviewError(err.message || t('complaintDetails.reviewError'));
      console.error('Error fetching review:', err);
    } finally {
      setReviewLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (complaint?.targetType === 'COMMENT' && complaint?.targetId && !review && !reviewLoading) {
      loadReview(complaint.targetId);
    }
  }, [complaint?.targetType, complaint?.targetId, review, reviewLoading, loadReview]);

  const handleOpenUser = (userId) => {
    if (!userId) return;
    navigate(`/users/${userId}`, {
      state: pushDetailNavState(location, { fromComplaint: location.pathname }),
    });
  };

  const handleTargetClick = () => {
    if (complaint?.targetId) {
      const returnPath = location.pathname;
      switch (complaint.targetType) {
        case 'USER':
          navigate(`/users/${complaint.targetId}`, {
            state: pushDetailNavState(location, { fromComplaint: returnPath }),
          });
          break;
        case 'ORDER':
          navigate(`/orders/${complaint.targetId}`, {
            state: pushDetailNavState(location, { fromComplaint: returnPath }),
          });
          break;
        case 'COMMENT':
          break;
        default:
          break;
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage({ type: 'success', text: t('userDetails.messages.copied', { defaultValue: 'Скопировано!' }) });
      setTimeout(() => setMessage(null), 2000);
    });
  };

  if (loading) {
    return (
      <div className="user-details-page">
        <div className="loading-state">
          <div className="strict-loader"></div>
          <div>{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-details-page">
        <div className="error-state">
          <div className="error-icon" style={{ color: '#ef4444', marginBottom: '16px' }}>
            <Icon name="alert-triangle" size={48} />
          </div>
          <h2 style={{ fontSize: '1.25rem', color: 'var(--text-primary)' }}>{t('complaintDetails.error')}</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="user-details-page">
        <div className="empty-state" style={{ padding: '4rem', textAlign: 'center' }}>
          <Icon name="search" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
          <h2 style={{ color: 'var(--text-primary)' }}>{t('complaintDetails.notFound')}</h2>
        </div>
      </div>
    );
  }

  const complaintShortId = complaint.id ? complaint.id.substring(0, 8) : 'N/A';
  const reporterShortId = complaint.reporterId ? complaint.reporterId.substring(0, 8) : 'N/A';
  const targetShortId = complaint.targetId ? complaint.targetId.substring(0, 8) : 'N/A';

  const renderProfileContent = (userData) => {
    if (!userData) return null;
    return (
      <div className="profile-header" style={{ gap: '16px', padding: '8px 0', display: 'flex', alignItems: 'center' }}>
        {userData.avatar ? (
          <ComplaintProfileAvatar src={userData.avatar} initials={userData.fullName} />
        ) : (
          <div className="sidebar-avatar-placeholder" style={{ width: '64px', height: '64px', fontSize: '1.5rem', flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent-primary)', color: 'white' }}>
            {userData.fullName?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
        <div className="profile-info" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
          <div className="profile-name" style={{ fontSize: '1.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 700 }}>
            {userData.fullName || t('users.labels.notProvided')}
          </div>
          <div className="profile-meta" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
              <Icon name="phone" size={14} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.9rem' }}>{userData.defaultPhoneNumber || t('users.labels.notProvided')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
              <Icon name="mail" size={14} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.9rem' }}>{userData.email || t('users.labels.notProvided')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderContent = (orderData) => {
    if (!orderData) return null;
    return (
      <div className="profile-header" style={{ gap: '16px', padding: '8px 0', display: 'flex', alignItems: 'center', height: '100%' }}>
        <div className="sidebar-avatar-placeholder" style={{ width: '64px', height: '64px', fontSize: '1.5rem', flexShrink: 0, borderRadius: '16px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="box" size={28} />
        </div>
        <div className="profile-info" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: 0 }}>
          <div className="profile-name" style={{ fontSize: '1.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            Заказ <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: '4px', color: 'var(--text-secondary)' }}>#{orderData.id.substring(0, 6)}</span>
          </div>
          <div className="profile-meta" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)', fontWeight: 600 }}>
              <Icon name="dollar-sign" size={14} style={{ color: '#10b981' }} />
              <span>{orderData.price != null ? `${orderData.price} MDL` : '—'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
              <StatusBadge type="order" status={orderData.status} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      className="user-details-page"
    >
      {message && (
        <div className={`message-toast ${message.type}`}>
          {message.text}
          <button className="message-close" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* DASHBOARD LAYOUT */}

      <div className="user-profile-dashboard">
        {/* === LEFT SIDEBAR === */}
        <aside className="profile-sidebar glass-panel">
          <div className="sidebar-header">
            <div className="sidebar-avatar-placeholder" style={{ background: 'linear-gradient(135deg, #ef4444, #f87171)' }}>
              <Icon name="alert-triangle" size={36} />
            </div>
            <h2 className="sidebar-name">{t('complaintDetails.title', { id: complaintShortId })}</h2>

            <div
              ref={statusAnchorRef}
              className="status-actions complaint-status-actions-anchor"
              style={{ position: 'relative', marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', zIndex: 100 }}
            >
              <StatusBadge type="complaint" status={complaint.status} className="status-badge--complaint-hero" />

              <button
                className="btn-icon hover-trigger"
                type="button"
                onClick={() => setShowStatusPopup(!showStatusPopup)}
                title={t('complaintDetails.changeStatus')}
                style={{ position: 'absolute', right: '0', padding: '6px', borderRadius: '8px' }}
              >
                <Icon name="edit-user" size={14} />
              </button>
            </div>

            {typeof document !== 'undefined' &&
              createPortal(
                <AnimatePresence>
                  {showStatusPopup && (
                    <motion.div
                      key="complaint-status-shell"
                      className="complaint-status-shell"
                      role="presentation"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <div
                        className="complaint-status-overlay"
                        onClick={() => {
                          setShowStatusPopup(false);
                          setSelectedStatus(complaint.status);
                        }}
                        aria-hidden
                      />
                      <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="complaint-status-popup-title"
                        className="complaint-status-popup complaint-status-popup--portal"
                        style={{ top: statusPopupPos.top, left: statusPopupPos.left }}
                        initial={{ opacity: 0, y: 40, scale: 0.9, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, scale: 1, x: '-50%' }}
                        exit={{ opacity: 0, y: 24, scale: 0.94, x: '-50%' }}
                        transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="complaint-status-popup__header">
                          <span id="complaint-status-popup-title">{t('complaintDetails.changeStatus')}</span>
                          <button
                            className="complaint-status-popup__close"
                            type="button"
                            onClick={() => {
                              setShowStatusPopup(false);
                              setSelectedStatus(complaint.status);
                            }}
                            aria-label={t('common.close')}
                          >
                            ×
                          </button>
                        </div>
                        <div className="complaint-status-popup__body">
                          <ComplaintStatusField
                            value={selectedStatus}
                            options={statusOptions}
                            onChange={setSelectedStatus}
                            disabled={updating}
                            placeholder={t('complaintDetails.selectStatusPlaceholder')}
                          />
                          <div className="complaint-status-popup__actions">
                            <button
                              className="btn btn-secondary"
                              type="button"
                              onClick={() => {
                                setShowStatusPopup(false);
                                setSelectedStatus(complaint.status);
                              }}
                              style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '0.9rem' }}
                            >
                              {t('common.cancel')}
                            </button>
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={handleStatusUpdate}
                              disabled={updating || !selectedStatus || selectedStatus === complaint.status}
                              style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '0.9rem' }}
                            >
                              {updating ? '...' : t('common.save')}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>,
                document.body
              )}
          </div>

          <div className="sidebar-quick-stats" style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderBottom: 'none', paddingBottom: '0' }}>

            {/* Карточка ID: Используем специальные CSS классы для точного позиционирования */}
            <div className="stat-item" style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', background: 'var(--bg-primary)', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <span className="id-label-fixed" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>ID:</span>
                <span className="mono id-value-fixed" style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: 600 }}>
                  {complaint.id}
                </span>
              </div>
              <button type="button" className="btn-copy hover-trigger" onClick={() => copyToClipboard(complaint.id)} title={t('userDetails.messages.copied')} style={{ position: 'absolute', top: '8px', right: '8px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', padding: '4px', color: 'var(--text-secondary)' }}>
                <Icon name="copy" size={16} />
              </button>
            </div>

            {/* Карточка Тип */}
            <div className="stat-item" style={{ flexDirection: 'column', alignItems: 'flex-start', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', background: 'var(--bg-primary)', gap: '6px' }}>
              <span className="stat-label" style={{ whiteSpace: 'normal', color: 'var(--text-secondary)' }}>
                {t('complaintDetails.labels.type')}:
              </span>
              <span className="stat-value" style={{ whiteSpace: 'normal', textAlign: 'left', lineHeight: '1.4', wordBreak: 'break-word', fontSize: '0.95rem', fontWeight: 600 }}>
                {targetTypeLabels[complaint.targetType] || complaint.targetType}
              </span>
            </div>

            {/* Карточка Дата Создания */}
            <div className="stat-item" style={{ flexDirection: 'column', alignItems: 'flex-start', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', background: 'var(--bg-primary)', gap: '6px' }}>
              <span className="stat-label" style={{ whiteSpace: 'normal', color: 'var(--text-secondary)' }}>
                {t('complaintDetails.labels.createdAt')}:
              </span>
              <span className="stat-value" style={{ whiteSpace: 'normal', textAlign: 'left', lineHeight: '1.4', wordBreak: 'break-word', fontSize: '0.95rem', fontWeight: 600 }}>
                {formatDate(complaint.createdAt)}
              </span>
            </div>

          </div>
        </aside>

        {/* === MAIN CONTENT AREA === */}
        <main className="profile-dashboard-main">
          <div className="bento-grid-layout complaints-bento-grid">

            {/* 1. Main Info */}
            <div className="bento-card bento-small">
              <div className="card-header">
                <h3 className="card-title">
                  <span className="card-icon-circle"><Icon name="info" size={13} /></span>
                  {t('complaintDetails.mainInfo', { defaultValue: 'Основная информация' })}
                </h3>
              </div>
              <div className="card-content">
                <InfoRow
                  label={t('complaintDetails.labels.type')}
                  value={targetTypeLabels[complaint.targetType] || complaint.targetType}
                />
                <InfoRow
                  label={t('complaintDetails.labels.createdAt')}
                  value={formatDate(complaint.createdAt)}
                />
                <InfoRow
                  label={t('complaintDetails.labels.updatedAt')}
                  value={formatDate(complaint.updatedAt)}
                />
              </div>
            </div>

            {/* COMPOSITE COLUMN: Reporter & Target Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* 1.1 Reporter Card */}
              <div 
                className="bento-card bento-small" 
                onClick={handleReporterClick}
                style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', flex: 1 }}
              >
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon-circle"><Icon name="user" size={13} /></span>
                    {t('complaintDetails.labels.reporter', { defaultValue: 'Репортер' })}
                  </h3>
                  <Icon name="chevron-right" size={16} style={{ opacity: 0.5 }} />
                </div>
                <div className="card-content" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {profilesLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><div className="strict-loader" style={{ width: '20px', height: '20px' }}></div></div>
                  ) : reporterData ? (
                    renderProfileContent(reporterData)
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <div style={{ wordBreak: 'break-all', color: 'var(--accent-primary)', fontSize: '1.1rem', fontWeight: 600, textAlign: 'center' }}>
                        {reporterShortId}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 1.2 Object (Target) Card */}
              <div 
                className="bento-card bento-small" 
                onClick={complaint.targetType !== 'COMMENT' ? handleTargetClick : undefined}
                style={{ cursor: complaint.targetType !== 'COMMENT' ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', flex: 1 }}
              >
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon-circle">
                      <Icon 
                        name={complaint.targetType === 'USER' ? 'user' : complaint.targetType === 'ORDER' ? 'box' : 'message-square'} 
                        size={13} 
                      />
                    </span>
                    {t('complaintDetails.labels.target', { defaultValue: 'Объект' })}
                  </h3>
                  {complaint.targetType !== 'COMMENT' && <Icon name="chevron-right" size={16} style={{ opacity: 0.5 }} />}
                </div>
                <div className="card-content" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                   {profilesLoading && complaint.targetType === 'USER' ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><div className="strict-loader" style={{ width: '20px', height: '20px' }}></div></div>
                  ) : (complaint.targetType === 'USER' && targetUserData) ? (
                    renderProfileContent(targetUserData)
                  ) : (complaint.targetType === 'ORDER' && targetOrderData) ? (
                    renderOrderContent(targetOrderData)
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <div style={{ wordBreak: 'break-all', color: complaint.targetType !== 'COMMENT' ? '#3b82f6' : 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 600, textAlign: 'center' }}>
                        {targetShortId}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* 2. Description */}
            <div className="bento-card bento-small description-card" style={{ gridColumn: 'span 1' }}>
              <div className="card-header">
                <h3 className="card-title">
                  <span className="card-icon-circle"><Icon name="file-text" size={13} /></span>
                  {t('complaintDetails.description')}
                </h3>
              </div>
              <div className="card-content">
                <div style={{
                  padding: '8px 0',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  color: 'var(--text-primary)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {complaint.description || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>{t('common.notProvided')}</span>}
                </div>
              </div>
            </div>

            {/* 3. Review (if target is COMMENT) */}
            {complaint.targetType === 'COMMENT' && (
              <div className="bento-card bento-large">
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon-circle"><Icon name="message-circle" size={16} /></span>
                    {t('complaintDetails.reviewTitle')}
                  </h3>
                </div>
                <div className="card-content">
                  {reviewLoading ? (
                    <div className="loading-state" style={{ padding: '2rem' }}><div className="strict-loader"></div></div>
                  ) : reviewError ? (
                    <div style={{ color: '#ef4444', padding: '1rem 0' }}>{reviewError}</div>
                  ) : review ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px' }}>
                      <div style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        padding: '16px',
                        background: 'var(--bg-secondary)'
                      }}>
                        <div style={{ fontSize: '0.95rem', lineHeight: '1.6', color: 'var(--text-primary)', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
                          {review.comment || <span style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>Без текста</span>}
                        </div>
                        {review.createdAt && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            {formatDate(review.createdAt)}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {review.reviewerId && (
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleOpenUser(review.reviewerId)}
                            style={{ fontSize: '0.85rem', padding: '8px 16px' }}
                          >
                            <Icon name="user" size={14} />
                            {t('complaintDetails.reviewButtons.viewReviewer')}
                          </button>
                        )}
                        {(review.reviewedUserId || review.reviewedProfileId) && (
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleOpenUser(review.reviewedUserId || review.reviewedProfileId)}
                            style={{ fontSize: '0.85rem', padding: '8px 16px' }}
                          >
                            <Icon name="company" size={14} />
                            {t('complaintDetails.reviewButtons.viewReviewed')}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* 4. Metadata (if exists) */}
            {complaint.metadata && Object.keys(complaint.metadata).length > 0 && (
              <div className="bento-card bento-large">
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon-circle"><Icon name="settings" size={13} /></span>
                    {t('complaintDetails.metadata')}
                  </h3>
                </div>
                <div className="card-content">
                  {Object.entries(complaint.metadata).map(([key, value]) => (
                    <InfoRow
                      key={key}
                      label={key}
                      value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    />
                  ))}
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </motion.div>
  );
};

export default ComplaintDetails;